import { LoadingButton } from '@mui/lab'
import { Box, Button, Divider, Grid, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { UserId } from '@cibo/core'
import { AssignUserControl, useAuth } from '@cibo/profile'
import { MapPinLaptop, logRequestError } from '@cibo/ui'
import { ascend, pluck, prop, sort } from 'ramda'

import { AxiosError } from 'axios'
import { useConfirmImports } from '../../../queries'
import { UploadFieldTable } from '../UploadFieldTable'

type ConfirmFieldsProps = {
  fields: any[]
  onConfirmed(importedFieldIds: string[]): void
  userId?: UserId
}

export const ConfirmFields = ({ fields, onConfirmed, ...props }: ConfirmFieldsProps) => {
  const { t } = useTranslation('@cibo/landmanager/features/UploadFlow')
  const navigate = useNavigate()
  const { persona } = useAuth()

  const confirmImports = useConfirmImports()

  const [edits, setEdits] = useState<Record<string, Record<string, any>>>({})
  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>()
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [userId, setUserId] = useState<UserId | undefined>(props.userId)
  const [fullText, setFullText] = useState<string>()
  const isGrower = persona === 'grower'

  const fieldsWithEdits = fields.map(field => {
    const fieldEdits = edits[field.id]
    if (fieldEdits) {
      return { ...field, ...fieldEdits }
    }
    return field
  })

  const handleConfirm = async () => {
    if (!selectedFieldIds) return
    try {
      const response = await confirmImports.mutateAsync({
        resourceIds: selectedFieldIds,
        owner: userId,
        resourceModifications: Object.keys(edits).map(resourceId => ({
          resourceId,
          ...edits[resourceId],
        })),
      })

      onConfirmed(pluck('resourceId')(response.data.items))
    } catch (error) {
      logRequestError(error as AxiosError)
    }
  }

  const sortedFields = sort(ascend(prop('alreadyExists')), fieldsWithEdits || [])

  return (
    <>
      <Grid container spacing={5}>
        <Grid container item xs={12} md={8}>
          <Grid item xs={12}>
            <Typography variant="h4" color="text.secondary">
              {t('confirmTitle')}
            </Typography>
            <Stack spacing={4}>
              <Typography variant="body1">{t('confirmSubhead')}</Typography>

              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {t('tableInstructions')}
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} md={12}>
            <Stack spacing={2}>
              <UploadFieldTable
                fields={sortedFields}
                selectedFieldIds={selectedFieldIds}
                setSelectedFields={setSelectedFieldIds}
                onEditsChanged={setEdits}
                setIsEditing={setIsEditing}
              />
              {!isGrower && (
                <AssignUserControl
                  setFullText={setFullText}
                  fullText={fullText}
                  userId={userId}
                  setUserId={setUserId}
                  disabled={!!props.userId}
                />
              )}
            </Stack>
          </Grid>
        </Grid>

        <Grid container item xs={4} md={4}>
          <MapPinLaptop />
        </Grid>
      </Grid>

      <Box m={2}>
        <Divider />
        <Stack m={5} spacing={5} direction="row" alignItems="left">
          <LoadingButton
            data-testid="submit_uploaded_fields"
            variant="contained"
            onClick={handleConfirm}
            disabled={
              (!isGrower && !userId) ||
              !selectedFieldIds ||
              selectedFieldIds.length === 0 ||
              isEditing
            }
            loading={confirmImports.isLoading}
          >
            {t('import')}
          </LoadingButton>
          <Button onClick={() => navigate(-1)}>{t('cancel')}</Button>
        </Stack>
        <Divider />
      </Box>
    </>
  )
}
